import { useCallback, useEffect, useMemo, useState } from 'react'
import { FiArrowDown, FiArrowUp, FiChevronRight } from 'react-icons/fi'
import { useParams } from 'react-router-dom'

import { Form } from '@unform/web'
import { t } from 'i18next'

import { Button } from '~/components/Buttons'
import Loading from '~/components/Loading'
import { InputMoney } from '~/components/unform'
import { formatMoneyV2, isAdvertiser, isPublisher, isReadOnly } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import useModal from '~/hooks/useModal'
import { useSidePanel } from '~/hooks/useSidePanel'

import { useMarketplaceBalance } from '../../hooks/useMarketplaceBalance'
import { usePaymentTypes } from '../../hooks/usePaymentTypes'
import { usePublisherWalletConfig } from '../../hooks/usePublisherWalletConfig'
import { CreateTransaction } from '../CreateTransaction'
import WalletSettings from '../WalletSettings'
import { MarketplaceBalance } from './components/MarketplaceBalance'
import { MarketplaceRechargeStatus } from './components/MarketplaceRechargeStatus'

import styles from './styles.module.css'

export const HeaderWalletBalance = ({
  balance = 0,
  debit,
  credit,
  initialLoading,
  publisher_id
}: {
  initialLoading?: boolean
  balance: string | number
  debit: string | number
  credit: string | number
  publisher_id?: string
}) => {
  const [initialData, setInitialData] = useState({ amount: 0 })

  const { id: checkingAccountId } = useParams()

  const [Modal, showModal, toggle] = useModal()
  const { addSidePanel, removeSidePanel } = useSidePanel()

  const {
    hasRecharge,
    rechargeEmail,
    lengthCentsCurrency,
    isWhiteLabel,
    name: PublisherName
  } = useAppInfo()

  const [hasMarketplace, setHasMarketplace] = useState<boolean>(false)
  const [marketplaceBalance, setMarketplaceBalance] =
    useState<getMarketplaceBalanceResponse | null>(null)
  const [loadingBalance, setLoadingBalance] = useState(true)

  const { data: paymentTypes } = usePaymentTypes()
  const { data: balanceData } = useMarketplaceBalance()
  const { data: publisherConfig } = usePublisherWalletConfig(publisher_id)

  const marketplacePayment = useMemo(() => {
    if (!Array.isArray(paymentTypes)) return null

    return paymentTypes.find(
      paymentType => paymentType.type === 'marketplace' && paymentType.active
    )
  }, [paymentTypes])

  const hasAutoRecharge = useMemo(() => {
    return publisherConfig?.auto_balance_transfer === 'enabled'
  }, [publisherConfig])

  useEffect(() => {
    if (!isWhiteLabel && !publisher_id) return

    setLoadingBalance(true)

    const isActive = !!marketplacePayment
    setHasMarketplace(isActive)

    if (isActive) {
      setMarketplaceBalance(balanceData as getMarketplaceBalanceResponse)
    } else {
      setMarketplaceBalance(null)
    }

    setLoadingBalance(false)
  }, [publisher_id, isWhiteLabel, marketplacePayment, balanceData])

  const handleSubmitTransactionInitiationGrocery = useCallback(
    (data: { amount: InputMoneyValues }) => {
      if (isPublisher()) {
        const amount = data.amount.numAsString
        const value = lengthCentsCurrency
          ? Number(amount) / 100
          : Number(amount)

        setInitialData({ amount: value })
      }

      toggle()
    },
    [lengthCentsCurrency, toggle]
  )

  const handleOpenSettings = useCallback(() => {
    if (!publisher_id) return

    addSidePanel({
      title: t('rm:marketplace.sidepanel.title'),
      type: 'content',
      content: (
        <WalletSettings
          publisherConfig={publisherConfig}
          publisherName={PublisherName}
          publisherId={publisher_id}
          onClose={removeSidePanel}
        />
      )
    })
  }, [
    addSidePanel,
    removeSidePanel,
    PublisherName,
    publisher_id,
    publisherConfig
  ])

  const showAdvertiserHowto = isAdvertiser() && !hasRecharge && !isReadOnly()
  const showPublisherForm = isPublisher() && !isReadOnly()

  const debitAmount = Math.abs(Number(debit) || 0)
  const creditNumber = Number(credit) || 0

  return (
    <>
      <div
        className={
          showPublisherForm
            ? `${styles.grid} ${styles.gridWithWide}`
            : styles.grid
        }
        data-component="HeaderWalletBalance"
      >
        <article className={styles.card}>
          <h4 className={styles.label}>{t('rm:Balance')}</h4>
          <Loading onlyLine status={!!initialLoading} />
          <p className={styles.value}>
            {initialLoading
              ? t('common:actions.loading')
              : formatMoneyV2({ value: balance || 0 })}
          </p>
          {hasMarketplace && !initialLoading ? (
            <div className={styles.foot}>
              <MarketplaceBalance
                initialLoading={!!initialLoading}
                loadingBalance={loadingBalance}
                hasMarketplace={hasMarketplace}
                marketplaceBalance={marketplaceBalance}
              />
              <MarketplaceRechargeStatus
                hasAutoRecharge={hasAutoRecharge}
                onOpenSettings={handleOpenSettings}
              />
            </div>
          ) : null}
        </article>

        {showAdvertiserHowto ? (
          <article className={styles.card}>
            <h4 className={styles.label}>
              {t('rm:wallet.table.actions.addCredit')}
            </h4>
            <p className={styles.copy}>
              {t('rm:recharge.howto')}{' '}
              <a href={`mailto:${rechargeEmail}`}>{rechargeEmail}</a>.
            </p>
          </article>
        ) : null}

        {showPublisherForm ? (
          <article className={`${styles.card} ${styles.cardWide}`}>
            <h4 className={styles.label}>
              {t('rm:wallet.table.actions.addCredit')}
            </h4>
            <Form
              onSubmit={handleSubmitTransactionInitiationGrocery}
              className={styles.rechargeForm}
            >
              <InputMoney
                id="amount"
                name="amount"
                placeholder={formatMoneyV2({ value: 0 })}
              />
              <Button
                iconRight={<FiChevronRight />}
                type="submit"
                text={t('common:actions.add')}
              />
            </Form>
          </article>
        ) : null}

        <article className={styles.card}>
          <h4 className={styles.label}>{t('rm:debits')}</h4>
          <p className={styles.period}>{t('rm:last7days')}</p>
          {debitAmount > 0 ? (
            <span className={`${styles.trend} ${styles.trendDebit}`}>
              <FiArrowDown aria-hidden />
              {formatMoneyV2({ value: debitAmount })}
            </span>
          ) : (
            <span className={`${styles.trend} ${styles.trendEmpty}`}>
              {t('rm:noTransactions')}
            </span>
          )}
        </article>

        <article className={styles.card}>
          <h4 className={styles.label}>{t('rm:credits')}</h4>
          <p className={styles.period}>{t('rm:last7days')}</p>
          {creditNumber > 0 ? (
            <span className={`${styles.trend} ${styles.trendCredit}`}>
              <FiArrowUp aria-hidden />
              {formatMoneyV2({ value: creditNumber })}
            </span>
          ) : (
            <span className={`${styles.trend} ${styles.trendEmpty}`}>
              {t('rm:noTransactions')}
            </span>
          )}
        </article>
      </div>

      <Modal
        title={t('rm:addCreditsWallet')}
        onClose={toggle}
        isOpen={!!showModal}
        maxWidth="100%"
      >
        {isPublisher() && (
          <CreateTransaction
            checkingAccountId={checkingAccountId}
            currentBalance={balance}
            parentInitialData={initialData}
            onClose={toggle}
          />
        )}
      </Modal>
    </>
  )
}
